import { useEffect, useRef, useState } from 'react';

/**
 * Follows `value`, but keeps each shown value up for at least `holdMs` so
 * text that changes faster than it can be read doesn't flicker. Values that
 * come and go within the hold are skipped; the latest one shows next.
 */
export const useSteadyValue = <T>(value: T, holdMs: number): T => {
  const [shown, setShown] = useState(value);
  const shownAtRef = useRef(Date.now());

  useEffect(() => {
    if (Object.is(value, shown)) {
      return;
    }
    const show = () => {
      shownAtRef.current = Date.now();
      setShown(value);
    };
    const wait = shownAtRef.current + holdMs - Date.now();
    if (wait <= 0) {
      show();
      return;
    }
    const timer = setTimeout(show, wait);
    return () => clearTimeout(timer);
  }, [value, shown, holdMs]);

  return shown;
};
